import { createContext, useContext, useEffect, useRef, useState } from 'react';
import loginFetch, { getUser, refresh as refreshTokenApi } from './Api/Login';
import jwt_decode from 'jwt-decode';
import moment from 'moment';
import 'moment/locale/fr';
import {
  clearStoredToken,
  getStoredToken,
  registerAuthSessionHandlers,
  setStoredToken,
} from './authSession';

/** For more details on
 * `authContext`, `ProvideAuth`, `useAuth` and `useProvideAuth`
 * refer to: https://usehooks.com/useAuth/
 */
const authContext = createContext();

export const AUTH_STATUS = {
  LOADING: 'loading',
  AUTHENTICATED: 'authenticated',
  ANONYMOUS: 'anonymous',
};

const AUTH_BOOTSTRAP_MIN_LOADING_MS = process.env.NODE_ENV === 'test' ? 0 : 5000;

function wait(delay) {
  return new Promise((resolve) => {
    setTimeout(resolve, delay);
  });
}

function decodeToken(token) {
  try {
    return jwt_decode(token);
  } catch (error) {
    return null;
  }
}

function getTokenExpiration(token) {
  const decodedToken = decodeToken(token);

  if (!decodedToken || !decodedToken.exp) {
    return null;
  }

  return moment.unix(decodedToken.exp);
}

function getRefreshDelay(token) {
  const expiration = getTokenExpiration(token);

  if (expiration === null) {
    return null;
  }

  const remainingMilliseconds = expiration.diff(moment());

  if (remainingMilliseconds <= 0) {
    return 0;
  }

  return Math.floor(remainingMilliseconds * 0.9);
}

export function ProvideAuth({ children }) {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

export function useAuth() {
  return useContext(authContext);
}

export function useProvideAuth()  {
  const initialToken = getStoredToken();
  const mountedRef = useRef(false);
  const refreshTimeoutRef = useRef(null);
  const refreshRequestRef = useRef(null);
  const sessionNonceRef = useRef(0);
  const [status, setStatus] = useState(
    initialToken ? AUTH_STATUS.LOADING : AUTH_STATUS.ANONYMOUS
  );
  const [loadingReason, setLoadingReason] = useState(initialToken ? 'bootstrap' : null);
  const [user, setUser] = useState(null);

  const clearRefreshTimeout = () => {
    if (refreshTimeoutRef.current !== null) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  };

  const ensureActiveSession = (sessionNonce) => {
    if (sessionNonce !== sessionNonceRef.current) {
      throw new Error('Session changed');
    }
  };

  const setAnonymousState = () => {
    if (mountedRef.current) {
      setUser(null);
      setStatus(AUTH_STATUS.ANONYMOUS);
      setLoadingReason(null);
    }
  };

  const setAuthenticatedState = (currentUser) => {
    if (mountedRef.current) {
      setUser(currentUser);
      setStatus(AUTH_STATUS.AUTHENTICATED);
      setLoadingReason(null);
    }
  };

  const setLoadingState = (reason) => {
    if (mountedRef.current) {
      setStatus(AUTH_STATUS.LOADING);
      setLoadingReason(reason);
    }
  };

  const clearSession = () => {
    sessionNonceRef.current += 1;
    clearRefreshTimeout();
    clearStoredToken();
    setAnonymousState();
  };

  const scheduleRefresh = (token) => {
    clearRefreshTimeout();

    const delay = getRefreshDelay(token);

    if (delay === null) {
      return;
    }

    refreshTimeoutRef.current = setTimeout(() => {
      refreshAccessToken().catch(() => {});
    }, delay);
  };

  const persistToken = async (token, sessionNonce = sessionNonceRef.current) => {
    const expiration = getTokenExpiration(token);

    if (expiration === null) {
      clearSession();
      throw new Error('Invalid token');
    }

    ensureActiveSession(sessionNonce);
    setStoredToken(token);
    scheduleRefresh(token);
  };

  const fetchCurrentUser = async (sessionNonce = sessionNonceRef.current) => {
    const response = await getUser();

    ensureActiveSession(sessionNonce);

    if (!response.ok) {
      throw new Error(`Unable to load user (${response.status})`);
    }

    const data = await response.json();

    ensureActiveSession(sessionNonce);

    if (!data.user) {
      throw new Error('User payload is missing');
    }

    return data.user;
  };

  const signin = async (payload) => {
    const sessionNonce = sessionNonceRef.current;

    clearRefreshTimeout();
    setLoadingState('signin');

    try {
      const response = await loginFetch(payload);

      ensureActiveSession(sessionNonce);

      if (!response.ok) {
        throw new Error(`Unable to authenticate (${response.status})`);
      }

      const data = await response.json();

      ensureActiveSession(sessionNonce);

      if (!data.token) {
        throw new Error('Authentication payload is missing token');
      }

      await persistToken(data.token, sessionNonce);
      const currentUser = await fetchCurrentUser(sessionNonce);
      setAuthenticatedState(currentUser);

      return data;
    } catch (error) {
      if (sessionNonce === sessionNonceRef.current) {
        clearSession();
      }

      throw error;
    }
  };

  const signout = () => {
    clearSession();
  };

  const isExpired = (token = getStoredToken()) => {
    const expiration = getTokenExpiration(token);

    if (expiration === null) {
      return true;
    }

    return !expiration.isAfter(moment());
  };

  const refreshAccessToken = async () => {
    if (refreshRequestRef.current !== null) {
      return refreshRequestRef.current;
    }

    const sessionNonce = sessionNonceRef.current;
    const request = (async () => {
      const token = getStoredToken();

      if (!token) {
        clearSession();
        throw new Error('No token to refresh');
      }

      try {
        const response = await refreshTokenApi();

        ensureActiveSession(sessionNonce);

        if (!response.ok) {
          throw new Error(`Unable to refresh token (${response.status})`);
        }

        const data = await response.json();

        ensureActiveSession(sessionNonce);

        if (!data.token) {
          throw new Error('Refresh payload is missing token');
        }

        await persistToken(data.token, sessionNonce);

        return data;
      } catch (error) {
        if (sessionNonce === sessionNonceRef.current) {
          clearSession();
        }

        throw error;
      }
    })();

    refreshRequestRef.current = request;

    try {
      return await request;
    } finally {
      if (refreshRequestRef.current === request) {
        refreshRequestRef.current = null;
      }
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    const unregisterAuthSessionHandlers = registerAuthSessionHandlers({
      refreshAccessToken,
      signout: clearSession,
    });

    const bootstrapSession = async () => {
      const token = getStoredToken();
      const sessionNonce = sessionNonceRef.current;

      if (!token) {
        setAnonymousState();
        return;
      }

      const minimumLoadingDelay = AUTH_BOOTSTRAP_MIN_LOADING_MS > 0
        ? wait(AUTH_BOOTSTRAP_MIN_LOADING_MS)
        : Promise.resolve();

      setLoadingState('bootstrap');

      try {
        if (isExpired(token)) {
          await refreshAccessToken();
          ensureActiveSession(sessionNonce);
        } else {
          await persistToken(token, sessionNonce);
        }

        const currentUser = await fetchCurrentUser(sessionNonce);
        await minimumLoadingDelay;
        ensureActiveSession(sessionNonce);
        setAuthenticatedState(currentUser);
      } catch (error) {
        if (sessionNonce === sessionNonceRef.current) {
          await minimumLoadingDelay.catch(() => {});
          clearSession();
        }
      }
    };

    bootstrapSession();

    return () => {
      mountedRef.current = false;
      unregisterAuthSessionHandlers();
      clearRefreshTimeout();
    };
  }, []);

  return {
    signin,
    signout,
    isExpired,
    refreshAccessToken,
    status,
    loadingReason,
    user,
    isAuthenticated: status === AUTH_STATUS.AUTHENTICATED,
  };
}
